import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks for customer operations
export const fetchCustomerProfile = createAsyncThunk(
  'ecommerceCustomer/fetchCustomerProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getCustomerProfile();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCustomerProfile = createAsyncThunk(
  'ecommerceCustomer/updateCustomerProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.updateCustomerProfile(profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAddresses = createAsyncThunk(
  'ecommerceCustomer/fetchAddresses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getAddresses();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createAddress = createAsyncThunk(
  'ecommerceCustomer/createAddress',
  async (addressData, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.createAddress(addressData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAddress = createAsyncThunk(
  'ecommerceCustomer/updateAddress',
  async ({ addressId, addressData }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.updateAddress(addressId, addressData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAddress = createAsyncThunk(
  'ecommerceCustomer/deleteAddress',
  async (addressId, { rejectWithValue }) => {
    try {
      await ecommerceService.deleteAddress(addressId);
      return addressId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  'ecommerceCustomer/setDefaultAddress',
  async (addressId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.setDefaultAddress(addressId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const registerCustomer = createAsyncThunk(
  'ecommerceCustomer/registerCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.registerCustomer(customerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const loginCustomer = createAsyncThunk(
  'ecommerceCustomer/loginCustomer',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.loginCustomer(credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const logoutCustomer = createAsyncThunk(
  'ecommerceCustomer/logoutCustomer',
  async (refreshToken, { getState, rejectWithValue }) => {
    const token = refreshToken || getState().ecommerceCustomer.tokens.refresh;
    try {
      const response = await ecommerceService.logoutCustomer(token);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// The storefront shares localStorage token keys with the ERP shell: `api`'s request
// interceptor and ProtectedRoute both read `token`. CUSTOMER_KEY tells a storefront
// customer session apart from an ERP staff session on reload.
const CUSTOMER_KEY = 'ecommerceCustomer';

const persistSession = ({ user, customer, tokens }) => {
  const profile = user || customer || null;
  if (tokens?.access) localStorage.setItem('token', tokens.access);
  if (tokens?.refresh) localStorage.setItem('refreshToken', tokens.refresh);
  if (profile) localStorage.setItem(CUSTOMER_KEY, JSON.stringify(profile));
  return profile;
};

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem(CUSTOMER_KEY);
};

// A JWT is only worth restoring while it is still in date. Presence alone is not enough:
// an expired access token would mark the customer authenticated, bounce them off
// /shop/login and /shop/register, then 401 on the first profile fetch — which the shared
// axios interceptor answers by redirecting to the ERP /auth/login. Net effect: the
// customer can neither reach their account nor create a new one.
const isTokenLive = (token) => {
  if (!token) return false;
  try {
    // JWT claims are base64url; atob only accepts standard base64.
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
};

const restoreSession = () => {
  const access = localStorage.getItem('token');
  const refresh = localStorage.getItem('refreshToken');
  try {
    const profile = JSON.parse(localStorage.getItem(CUSTOMER_KEY));
    // Keep the session when the refresh token is still live even if `access` has aged
    // out — the interceptor transparently renews it on the next call.
    if (profile && (isTokenLive(access) || isTokenLive(refresh))) {
      return { profile, isAuthenticated: true, tokens: { access, refresh } };
    }
  } catch (e) {
    // fall through to the signed-out state below
  }
  // Drop the dead customer session so /shop/register stays reachable. Only when both
  // tokens are truly expired — `token`/`refreshToken` are shared with the ERP shell and
  // must never be cleared out from under a live staff session.
  if (localStorage.getItem(CUSTOMER_KEY) && !isTokenLive(access) && !isTokenLive(refresh)) {
    clearSession();
  }
  return { profile: null, isAuthenticated: false, tokens: { access: null, refresh: null } };
};

const initialState = {
  ...restoreSession(),
  addresses: [],
  loading: false,
  addressLoading: false,
  error: null,
  successMessage: null,
};

const ecommerceCustomerSlice = createSlice({
  name: 'ecommerceCustomer',
  initialState,
  reducers: {
    setAuthTokens: (state, action) => {
      state.tokens = action.payload;
      state.isAuthenticated = true;
    },
    clearAuthTokens: (state) => {
      clearSession();
      state.tokens = { access: null, refresh: null };
      state.isAuthenticated = false;
      state.profile = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Customer Profile
      .addCase(fetchCustomerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchCustomerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Customer Profile
      .addCase(updateCustomerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.successMessage = 'Profile updated successfully';
      })
      .addCase(updateCustomerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Addresses
      .addCase(fetchAddresses.pending, (state) => {
        state.addressLoading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.addressLoading = false;
        // The endpoint answers {count, addresses: [...]}; DRF pagination would use
        // {results: [...]}. Never assign the envelope itself — consumers iterate this.
        const payload = action.payload;
        state.addresses = Array.isArray(payload)
          ? payload
          : payload?.addresses || payload?.results || [];
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.addressLoading = false;
        state.error = action.payload;
      })

      // Create Address
      .addCase(createAddress.pending, (state) => {
        state.addressLoading = true;
        state.error = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.addressLoading = false;
        state.addresses.push(action.payload);
        state.successMessage = 'Address added successfully';
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.addressLoading = false;
        state.error = action.payload;
      })

      // Update Address
      .addCase(updateAddress.pending, (state) => {
        state.addressLoading = true;
        state.error = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.addressLoading = false;
        const index = state.addresses.findIndex(addr => addr.id === action.payload.id);
        if (index !== -1) {
          state.addresses[index] = action.payload;
        }
        state.successMessage = 'Address updated successfully';
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.addressLoading = false;
        state.error = action.payload;
      })

      // Delete Address
      .addCase(deleteAddress.pending, (state) => {
        state.addressLoading = true;
        state.error = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.addressLoading = false;
        state.addresses = state.addresses.filter(addr => addr.id !== action.payload);
        state.successMessage = 'Address deleted successfully';
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.addressLoading = false;
        state.error = action.payload;
      })

      // Set Default Address
      .addCase(setDefaultAddress.pending, (state) => {
        state.addressLoading = true;
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.addressLoading = false;
        // Update all addresses, setting only the selected one as default
        state.addresses = state.addresses.map(addr => ({
          ...addr,
          is_default: addr.id === action.payload.id,
        }));
        state.successMessage = 'Default address updated';
      })

      // Register Customer
      .addCase(registerCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = persistSession(action.payload);
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.successMessage = action.payload.message || 'Registration successful';
      })
      .addCase(registerCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Login Customer
      .addCase(loginCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = persistSession(action.payload);
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.successMessage = action.payload.message || 'Login successful';
      })
      .addCase(loginCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout Customer — the local session is dropped even if blacklisting the
      // refresh token failed, otherwise a server error strands the user signed in.
      .addMatcher(
        (action) => action.type === logoutCustomer.fulfilled.type
          || action.type === logoutCustomer.rejected.type,
        (state) => {
          clearSession();
          state.profile = null;
          state.tokens = { access: null, refresh: null };
          state.isAuthenticated = false;
          state.addresses = [];
          state.successMessage = 'Logged out successfully';
        }
      );
  },
});

export const {
  setAuthTokens,
  clearAuthTokens,
  clearError,
  clearSuccessMessage,
} = ecommerceCustomerSlice.actions;

export default ecommerceCustomerSlice.reducer;
